import { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import { TRANSACTION_TYPE_OPTIONS } from "../../utils/enum";
import SearchableSelect from "../Common/SearchableSelect";
import debounce from "lodash/debounce";
import Paggination from "../Paggination";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";

const formatDisplayDate = (val) => {
  if (!val) return "—";
  const d = dayjs(val);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};

const isDateLike = (val) => {
  if (!val || typeof val !== "string") return false;
  return (
    dayjs(val).isValid() || val.includes("GMT") || /\d{4}-\d{2}-\d{2}/.test(val)
  );
};

const LedgerModal = ({
  show,
  onClose,
  accountName = "",
  accountId = "",
  accountDbId,
}) => {
  const { AsyncGetApiCall } = useApi();
  const [query, setQuery] = useState({
    search: "",
    pageNo: 1,
    limitVal: 10,
    accountId: accountDbId,
    transactionType: "all"
  });

  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);

  const setPageNo = (pageNo) => {
    setQuery((prev) => ({
      ...prev,
      pageNo,
    }));
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value)
    debouncedSearch(value);
  };

  const formatAmount = (amount) => {
    const num = Number(amount) || 0;
    if (num === 0) return "0";
    return Math.abs(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (show && (accountDbId || accountId)) {
      fetchFullLedger();
    } else if (!show) {
      setLedgerEntries([]);
      setError(null);
      setQuery((prev) => ({ ...prev, search: "", transactionType: "all", pageNo: 1 }));
      setSearchTerm("")
      setApiResponse(null);
    }
  }, [show, accountDbId, accountId, query]);

  const fetchFullLedger = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await AsyncGetApiCall("/accounts/get-full-ledger", {
        accountId: accountDbId,
        transactionType: query.transactionType,
        search: query.search,
        pageNo: query.pageNo,
        limitVal: query.limitVal
      });

      if (result?.errors) {
        setError(result.errors[0] || "Failed to fetch ledger");
        setLedgerEntries([]);
        setApiResponse(null);
      } else if (result?.success && Array.isArray(result.data)) {
        const mapped = result.data.map((item) => {
          const credit = item?.credit > 0 ? item?.credit : 0;
          const debit = item?.debit > 0 ? `-${item?.debit}` : 0;

          return {
            id: item?._id,
            title: item?.name || "Ledger Entry",
            date: item?.createdAt,
            credit: item?.name === "Opening Balance" || item?.name === "Closing Balance" ? "-" : formatAmount(credit),
            debit: item?.name === "Opening Balance" || item?.name === "Closing Balance" ? "-" : formatAmount(debit),
            balance:
              item?.balance > 0 || item?.name === "Opening Balance" || item?.name === "Closing Balance"
                ? formatAmount(item?.balance)
                : `-${formatAmount(item?.balance)}`,
            isSettlement: item?.isSettlement,
            from: item?.from,
            to: item?.to,
            color: item?.balance > 0 ? "text-success" : "text-danger",
            userId: item?.userId,
          };
        });

        setLedgerEntries(mapped);
        setApiResponse(result);
      } else {
        setError(result?.message || "No data received");
        setApiResponse(null);
      }
    } catch (err) {
      setError("An error occurred while fetching ledger data");
      setLedgerEntries([]);
      setApiResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (entry) => {
    try {
      if (!entry.from || !entry.to) return;

      const response = await AsyncGetApiCall(
        `/accounts/generate-invoice?from=${encodeURIComponent(entry?.from)}&to=${encodeURIComponent(entry?.to)}&targetUserId=${entry?.userId}`,
      );

      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.open();
        newWindow.document.write(response?.data);
        newWindow.document.close();
      }
    } catch (error) {
      console.error("Failed to open invoice", error);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 fw-bold">
          <span className="d-flex align-items-center gap-2">
            <Icon icon="tabler:file-text" className="icon text-xl" />
            Ledger Statement
          </span>
          <span className="fw-semibold fs-6 text-secondary ms-sm-3">
            {accountName} ({accountId})
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 m-2">
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3 mb-8">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div style={{ minWidth: "160px", maxWidth: "200px" }}>
              <SearchableSelect
                placeholder="Select Type"
                value={query.transactionType}
                showSearch={false}
                onChange={(e) => {
                  setQuery({ ...query, transactionType: e.target.value, pageNo: 1 })
                }}
                options={TRANSACTION_TYPE_OPTIONS.map((n) => ({
                  label: String(n.label),
                  value: String(n.value),
                }))}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-md fw-medium text-secondary mb-0">Show</span>
              <div style={{ width: "90px" }}>
                <SearchableSelect
                  placeholder="Show"
                  value={String(query.limitVal)}
                  showSearch={false}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setQuery((prev) => ({
                      ...prev,
                      limitVal: value,
                      pageNo: 1,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className="position-relative flex-grow-1"
            style={{ maxWidth: "280px" }}
          >
            <Icon
              icon="ion:search-outline"
              className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
            />
            <input
              type="text"
              placeholder="Search by name..."
              className="form-control ps-40 h-40-px radius-12"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="table-responsive border rounded-2 overflow-hidden px-4 overflow-y-auto" style={{ maxHeight: "70vh" }}>
          <table className="table table-hover align-middle mb-0 ">
            <thead className="position-sticky top-0" style={{ zIndex: 2 }}>
              <tr className="bg-neutral-100">
                <th
                  className="px-4 py-3 fw-semibold text-slate-800"
                  style={{ width: "50px", position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  SR
                </th>
                <th
                  className="px-4 py-3 fw-semibold text-slate-800"
                  style={{ position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  NAME / DESCRIPTION
                </th>
                <th
                  className="px-4 py-3 fw-semibold text-slate-800"
                  style={{ width: "120px", position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  DATE
                </th>
                <th
                  className="px-4 py-3 fw-semibold text-slate-800 text-end"
                  style={{ width: "120px", position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  DEBIT (₹)
                </th>
                <th
                  className="px-4 py-3 fw-semibold text-slate-800 text-end"
                  style={{ width: "120px", position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  CREDIT (₹)
                </th>
                <th
                  className="px-4 py-3 fw-semibold text-slate-800 text-end"
                  style={{ width: "140px", position: "sticky", top: 0, backgroundColor: "var(--neutral-100)" }}
                >
                  BALANCE (₹)
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-5 text-center">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    />
                    <p className="mt-2 text-secondary mb-0">
                      Loading ledger entries...
                    </p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="py-5 text-center">
                    <p className="text-danger fw-medium mb-0">{error}</p>
                  </td>
                </tr>
              ) : ledgerEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-5 text-center text-secondary">
                    No matching ledger entries found
                  </td>
                </tr>
              ) : (
                ledgerEntries.map((entry, index) => (
                  <tr key={entry._id}>
                    <td className="px-4 py-3 text-secondary fw-medium">
                      {((apiResponse?.currentPage || 1) - 1) * query.limitVal + index + 1}
                    </td>
                    <td className={`px-4 py-3 ${entry?.title?.toLowerCase()?.includes('ongoing settlement') ? 'fw-bold' : ''}`}>
                      <div>
                        <span className="text-slate-800">{entry?.title}</span>
                        {entry.isSettlement && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-primary text-decoration-none ms-2 align-baseline"
                            onClick={() => handleViewDetails(entry)}
                            title="View Details"
                          >
                            <Icon
                              icon="tabler:file-text"
                              className="icon text-lg"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-secondary">{entry.date}</td>
                    <td className="px-4 py-3 text-end text-danger fw-medium font-monospace">
                      {entry?.debit}
                    </td>
                    <td className="px-4 py-3 text-end text-success fw-medium font-monospace">
                      {entry?.credit}
                    </td>
                    <td className="px-4 py-3 text-end fw-semibold font-monospace">
                      <span className={entry?.color}>{entry?.balance}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Paggination
            total={apiResponse?.total || 0}
            totalPages={apiResponse?.totalPages || 0}
            currentPage={apiResponse?.currentPage || 0}
            setPageNo={setPageNo}
            startFrom={apiResponse?.total > 0 ? ((apiResponse?.currentPage || 1) - 1) * query.limitVal + 1 : 0}
            endTo={
              apiResponse?.total > 0
                ? ((apiResponse?.currentPage || 1) - 1) * query.limitVal + (ledgerEntries?.length || 0)
                : 0
            }
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LedgerModal;
